import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { PlateCanvas } from 'classes/plate-canvas';
import { ErrorBoundary } from 'components/common/error-boundary';
import env from 'config';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { BALL_DIAMETER_IN, PlateHelper } from 'lib_ts/classes/plate.helper';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { IPitchDetails } from 'lib_ts/interfaces/pitches/i-common-pitch-recap';
import { useEffect, useMemo, useRef, useState } from 'react';
import useMiscStore from 'stores/use-misc-store';
import './plate-view.scss';

const REDRAW_DEQUEUE_MS = 100;

interface IProps {
  pitchDetails: Nullable<IPitchDetails> | undefined;
  strikeZone: IStrikeZone;
  /** When true, fills container height without enforcing aspect ratio */
  fillContainer?: boolean;
}

export const PitchRecapPlate = (props: IProps) => {
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const mainCanvas = useRef<HTMLCanvasElement>(null);
  const shotCanvas = useRef<HTMLCanvasElement>(null);

  const [imgSrc, setImgSrc] = useState<string>();

  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  const canvasDef = useMemo(
    () => PlateCanvas.makeWidget(props.strikeZone, isDarkMode),
    [props.strikeZone, isDarkMode]
  );

  useEffect(() => {
    setLastUpdate(Date.now());
  }, [canvasDef]);

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    updateTimeout.current = setTimeout(async () => {
      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [mainCanvas.current, shotCanvas.current].filter(
        (c) => c
      ) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  // drawMain
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = mainCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // console.debug('drawMain');

        canvasDef.drawStrikeZone(ctx);
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [canvasDef]);

  // drawOutcome
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = shotCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!props.pitchDetails?.strikeZone?.plateFt) {
          return;
        }

        const { plate_x, plate_z } = props.pitchDetails.strikeZone.plateFt;

        if (plate_x === undefined) {
          return;
        }

        if (plate_z === undefined) {
          return;
        }

        // console.debug('drawOutcome');

        const isStrike = PlateHelper.isPlateInZone({
          plateFt: props.pitchDetails.strikeZone.plateFt,
          szDef: canvasDef.CONFIG.zone,
          // up to half of the ball could be outside the SZ and still be a strike
          bufferFt: BALL_DIAMETER_IN / FT_TO_INCHES / 2,
        });

        await canvasDef.drawOutcome(
          ctx,
          {
            plate_x: plate_x,
            plate_z: plate_z,
          },
          isStrike
        );
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [props.pitchDetails, canvasDef]);

  return (
    <ErrorBoundary identifier="PitchRecapPlate">
      <div
        className="PlateWidget"
        style={
          props.fillContainer
            ? { height: '100%' }
            : { aspectRatio: canvasDef.CONFIG.canvas.aspectRatio }
        }
      >
        {/* for log rocket recordings to show the results */}
        {imgSrc && (
          <img
            title={
              env.identifier === 'local'
                ? [
                    `X: ${
                      props.pitchDetails?.strikeZone?.plateFt?.plate_x.toFixed(
                        2
                      ) ?? '???'
                    }`,
                    `Z: ${
                      props.pitchDetails?.strikeZone?.plateFt?.plate_z.toFixed(
                        2
                      ) ?? '???'
                    }`,
                  ].join('\n')
                : undefined
            }
            src={imgSrc}
          />
        )}
        <canvas
          ref={mergeCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
        <canvas
          ref={mainCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
        <canvas
          ref={shotCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
      </div>
    </ErrorBoundary>
  );
};
